import { track } from './consent'

// Browser geolocation for "cheapest near you", with the last-known position
// persisted in local storage so returning users aren't re-prompted every visit.
// This is the ONLY location path — if the user declines or geolocation is
// unavailable, results fall back to national prices (no city picker).

const STORAGE_KEY = 'geoLocation'
// Set when the user clears the location. The position itself expires after 24h,
// so without a separate flag a granted permission would silently re-acquire on
// the next visit and "Poništi" would look broken.
const OPT_OUT_KEY = 'geoOptOut'
// A saved position expires so we don't silently claim "prices near you" from a
// weeks-old location (e.g. scanned in Zagreb, now in Split).
const MAX_AGE_MS = 24 * 60 * 60 * 1000

// Coordinates are rounded to a 0.02 degree grid — roughly 2 km, and nothing
// finer is ever stored, cached or sent. products_controller.rb already caches
// on round(2) and the default radius is 50 km, so no result changes, and it is
// what makes "approximate location" true on the Play Data safety form.
const GRID_DEG = 0.02

export function roundCoord(value) {
  // toFixed because 2291 * 0.02 is 45.82000000000001 in binary floating point,
  // and that lands in a query string.
  return Number((Math.round(value / GRID_DEG) * GRID_DEG).toFixed(2))
}

export function getSavedLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const loc = JSON.parse(raw)
    if (typeof loc?.lat !== 'number' || typeof loc?.lon !== 'number') return null
    if (loc.ts && Date.now() - loc.ts > MAX_AGE_MS) return null // stale → re-prompt
    return { lat: loc.lat, lon: loc.lon }
  } catch {
    return null
  }
}

export function saveLocation(loc) {
  try {
    if (loc) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat: loc.lat, lon: loc.lon, ts: Date.now() }))
      localStorage.removeItem(OPT_OUT_KEY) // asking for location undoes a prior opt-out
    } else {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.setItem(OPT_OUT_KEY, '1') // clearing IS the opt-out signal
    }
  } catch {
    /* ignore */
  }
  return loc ? { lat: loc.lat, lon: loc.lon } : null
}

// Set once the browser dialog has been fired automatically after a first
// result, whatever the user answered. It makes that one prompt per visitor
// rather than one per result, and it is not an opt-out: the CTA in the
// location bar stays and a later tap still asks.
const AUTO_ASKED_KEY = 'geoAutoAsked'

export function hasAutoAskedLocation() {
  try {
    return localStorage.getItem(AUTO_ASKED_KEY) === '1'
  } catch {
    return false
  }
}

export function markLocationAutoAsked() {
  try {
    localStorage.setItem(AUTO_ASKED_KEY, '1')
  } catch {
    /* ignore */
  }
}

export function isLocationOptedOut() {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1'
  } catch {
    return false
  }
}

export function setLocationOptOut(value) {
  try {
    if (value) localStorage.setItem(OPT_OUT_KEY, '1')
    else localStorage.removeItem(OPT_OUT_KEY)
  } catch {
    /* ignore */
  }
}

// 'granted' | 'prompt' | 'denied' | 'unknown'. Mirrors the camera check in
// ScannerPage. 'unknown' is the safe default: it takes the same path as
// 'prompt', so an unsupported Permissions API just keeps the old behaviour.
export async function queryLocationPermission() {
  if (!navigator.permissions?.query) return 'unknown'
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' })
    return result.state
  } catch {
    return 'unknown'
  }
}

// Ask the browser for the current position. Resolves { lat, lon } or rejects
// (permission denied / unsupported / timeout).
//
// The three events here are the grant-rate measurement. Until now that number
// only existed as `?lat=` in the server log, which cannot separate a denial
// from a browser that was never asked.
export function requestLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      track('location_unsupported')
      reject(new Error('geolocation-unsupported'))
      return
    }
    track('location_prompt')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        track('location_granted')
        resolve({ lat: roundCoord(pos.coords.latitude), lon: roundCoord(pos.coords.longitude) })
      },
      (err) => {
        track('location_denied', { reason: err?.code === 1 ? 'denied' : 'error' })
        reject(err)
      },
      { timeout: 8000, maximumAge: 5 * 60 * 1000 }
    )
  })
}
